<template>
  <div>
    <V2MoleculesHomeHeroHeader>
      <template #main-container>
        <div class="w-full max-w-8xxl flex items-center justify-between md:px-10">
          <img
            src="/images/hero/home-page-left.png"
            width="380"
            height="500"
            class="hidden 2xl:block"
          />
          <div class="w-full">
            <V2MoleculesReviews
              class="!py-0 !min-h-fit bg-transparent"
              size="base"
              :inverted="true"
              :hide-star-string="true"
              star-type="component"
              star-color="default"
              :fill-color="semanticColorLookup('inverse.primary')"
            />
            <div class="md:my-6 my-3">
              <div
                class="text-semantic-inverse-on-surface text-center font-normal md:text-[96px]/[100%] text-[60px]/[100%] font-hero uppercase xl:text-nowrap"
              >
                YOUR LIFE'S
                <span class="text-semantic-inverse-primary">INCOMPARABLE</span>
              </div>
              <div
                class="text-center text-semantic-inverse-on-surface md:text-[60px]/[100%] text-4xl/[100%] font-hero font-normal uppercase"
              >
                AND YOUR COVER SHOULD BE TOO.
              </div>
            </div>
            <div
              class="text-center text-semantic-primary-on-default font-normal md:text-lg/[150%] text-base/[150%]"
            >
              We help you save on cover that fits you — not the average John or
              Jane.
            </div>

            <MoleculesHomeCardItems :cards="CARD_ITEMS" />
          </div>
          <img
            src="/images/hero/home-page-right.png"
            width="350"
            height="500"
            class="hidden 2xl:block"
          />
        </div>
      </template>
    </V2MoleculesHomeHeroHeader>

    <V2OrganismHomeCompanyComparison
      :assets-base-url="`/icons/home-banner/company-logos/`"
      :companies="PARTNER_ITEMS"
      :hide-scroll-bar="true"
      :card-width="100"
      :show-more="false"
      max-width="max-w-wrapper-lg"
      class="!bg-semantic-surface-container-lowest"
    >
      <template #header> Meet our Partners </template>
      <template #sub-header>
        We work with a wide range of leading insurance providers. Unfortunately
        some funds choose not to be listed, but we'll still help you find the
        best value from those who are.
      </template>
    </V2OrganismHomeCompanyComparison>

    <V2OrganismHomeMediaBlock
      title="As seen on the news. Trusted by Aussies."
      description="When trusted media outlets need expert insights on insurance, finance and utilities they turn to us. Our experts help Aussies make smarter comparisons - saving time, stress and money."
    >
      <div
        class="flex items-center justify-around gap-10 !py-4 md:!my-8 !my-4 overflow-auto bg-semantic-surface-container-low"
      >
        <div
          v-for="(item, itemKey) in outlets"
          :key="itemKey"
          class="exact-w-[6.375rem] h-fit"
        >
          <img v-bind="item" class="m-auto text-semantic-surface-on-surface" />
        </div>
      </div>
    </V2OrganismHomeMediaBlock>

    <V2OrganismHomeWhyCompareClubSection />

    <V2MoleculesReviews
      layout="reviews"
      size="large"
      star-type="component"
      inverted
      star-color="default"
      ratings-class="!mt-3"
      hide-star-string
      stars="4,5"
      :fill-color="semanticColorLookup('inverse.primary')"
      class="!min-h-fit bg-semantic-inverse-surface-light"
    >
      <template #header="{ data }">
        <V2AtomsLabel> {{ data.trustScore }} ★ on Trustpilot </V2AtomsLabel>
        <div
          class="text-semantic-inverse-on-surface font-semibold md:text-4xl/[120%] text-2xl/[120%]"
        >
          Rated and recommended by Aussies
        </div>
      </template>

      <template #slider="{ reviewList }">
        <swiper-slide
          v-for="(item, itemKey) in reviewList"
          :key="itemKey"
          class="w-auto relative drop-shadow-sm-2 gap-8 !inline-flex flex-col items-start flex-grow"
        >
          <V2MoleculesReviewsFeatureReviewCard
            :key="itemKey"
            :title="item.title"
            :sub-title="`${item.customer} - ${toHours(item.createdAt)}`"
            :description="item.text"
            :review="item.starValue"
            class="w-auto max-w-[30rem] lg:!min-w-[30rem] min-w-[22rem] lg:!h-[18rem] md:!h-[17.5rem]  h-[17.5rem]"
            cta-btn-label="Read on Trustpilot"
            :cta-btn-to="`https://au.trustpilot.com/reviews/${item.id}`"
            cta-btn-target="_blank"
          />
        </swiper-slide>
      </template>
    </V2MoleculesReviews>

    <V2OrganismHomeVerticalSection
      title="Your life. Your goals. Your savings."
      description="From big moves to everyday bills, we help you save again and again on what matters to you. "
      :list="VERTICALS_LIST"
    />

    <V2OrganismHomeArticleSection
      title="Smart explainers made for busy people"
      description="We turn the complicated stuff into clear, practical advice, so you can make the right decisions for you and your life."
    />

    <V2OrganismFaq
      id="faq"
      title="Got questions? Let's clear things up."
      :list="FAQ_ITEMS"
    />

    <V2MoleculesCardDisclaimerCard
      class="!bg-semantic-surface-container-variant"
    >
      <template #title> Things you should know </template>
      <template #description>
        <p>
          As our customer you'll be provided with quotes directly from the insurer for the product you intend to purchase.
          We manage the application and deal with the administration work and insurer.
          We do not charge you a fee for the service we provide, the insurer simply remunerates us in return for setting up your policy.
        </p>
        <br />

        <p>
          The products compared on this website do not necessarily compare all features
          that may be relevant to you and different products may have different features and
          different levels of coverage. Comparisons on health insurance products are made on the basis of price or fit;
          comparisons on all other products/services are made on the basis of price only.
          Compare Club does not compare all policies or products available in Australia and our product partners
          may not make all policies or products available to Compare Club.
        </p>
        <br />
      </template>
    </V2MoleculesCardDisclaimerCard>
  </div>
</template>

<script setup>
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import _isEmpty from 'underscore/cjs/isEmpty.js'
import {
  PARTNER_ITEMS,
  FAQ_ITEMS,
  CARD_ITEMS,
  VERTICALS_LIST
} from '~/constants/pages/home-page'

const layoutsStore = useLayoutsStore()

const navProps = layoutsStore.navigation
navProps.hasShadow = true

defineOptions({
  name: 'Home'
})

const $route = useRoute()

try {
  dayjs.extend(relativeTime)
} catch (error) {
  throw new Error(error?.message ?? String(error), { cause: error })
}

function toHours (date) {
  return dayjs(date).fromNow()
}

const mediaOutlets = computed(() => {
  return [
    {
      url: `${APP_ASSET_BASEURL}images/logos/variant/abc-news.svg`,
      width: 88.99,
      height: 18.99
    },
    {
      url: `${APP_ASSET_BASEURL}images/logos/variant/9news.svg`,
      width: 106,
      height: 18.99
    },
    {
      url: `${APP_ASSET_BASEURL}images/logos/variant/7news.svg`,
      width: 83,
      height: 18.99
    },
    {
      url: `${APP_ASSET_BASEURL}images/logos/variant/kiis.svg`,
      width: 43,
      height: 18.99
    },
    {
      url: `${APP_ASSET_BASEURL}images/logos/variant/hearld-sun.svg`,
      width: 98,
      height: 16
    },
    {
      url: `${APP_ASSET_BASEURL}images/logos/variant/daily-telegraph.svg`,
      width: 98,
      height: 15
    },
    {
      url: `${APP_ASSET_BASEURL}images/logos/variant/courier-mail.svg`,
      width: 98,
      height: 13.99
    },
    {
      url: `${APP_ASSET_BASEURL}images/logos/variant/financial-review.svg`,
      width: 116.01,
      height: 14.3
    }
  ]
})

const outlets = computed(() => {
  const list = mediaOutlets.value

  if (_isEmpty(list)) {
    return []
  }

  return list.map(item => {
    const urlArr = item.url.split('/')
    const alt = urlArr[urlArr.length - 1]
      .replace(/.(svg|png|jpg|jpeg|webp|gif)$/g, '')
      .replace(/(-|_)/gm, ' ')

    return {
      ...((item?.url && { src: item.url }) || {}),
      ...((alt && { alt }) || {}),
      ...((item?.width && { width: item.width }) || {}),
      ...((item?.height && { height: item.height }) || {}),
      ...({ loading: 'lazy' } || {})
    }
  })
})

const PAGE_DETAILS = {
  title: 'Compare Club: Health & Life Insurance, Energy, Loans & More',
  description: 'Do your household bills need saving? Compare, change and save money on your health insurance, energy bills, home loans and life cover',
  ogDescription: 'Save money on your household expenses. We compare health and life insurance, home loans, energy plans, childcare and much more',
  ogUrl: trailingSlash(CC_DOMAIN),
  ogType: 'homepage'
}

generateMeta({
  title: PAGE_DETAILS.title,
  ogTitle: PAGE_DETAILS.title,
  description: PAGE_DETAILS.description,
  ogDescription: PAGE_DETAILS.ogDescription,
  ogUrl: PAGE_DETAILS.ogUrl,
  ogType: PAGE_DETAILS.ogType
}, pageIsFunnel($route))

useJsonld(() => {
  const path = $route.path

  return {
    '@context': 'https://schema.org',
    '@graph': [
      GENERATE_WEBPAGE_SCHEMA({
        page: path,
        pageTitle: PAGE_DETAILS.title,
        pageDescription: PAGE_DETAILS.ogDescription,
        aboutPath: `${path}#organization`,
        primaryImageOfPagePath: '/#logo',
        breadcrumbPath: `${path}#breadcrumb`
      }),
      GENERATE_BREADCRUMB_SCHEMA(path)
    ]
  }
})
</script>
